import Button, { ButtonVariants } from '@/components/atoms/legacy/Button';
import Link from '@/components/atoms/legacy/Link';
import FormInput from '@/components/molecules/FormInput';
import FormTextarea from '@/components/molecules/FormTextarea';
import { ProgressBar } from '@/components/molecules/ProgressBar/ProgressBar';
import {
  ContactInformationFormFields,
  PartnerSignUpFormFields,
} from '@/components/template/PartnerLandingTemplate/types';
import { validateEmail } from '@/lib/validators';
import { validatePhone } from '@/lib/validators/phone';
import { FormErrors, isFormValid } from '@/types/enums';
import React, { useCallback } from 'react';

export type ContactInformationFormType = {
  firstName: string;
  lastName: string;
  phone: string;
  extension: string;
  email: string;
  comment: string;
};

type ContactInformationFormActionType =
  | { type: 'UPDATE_FIRST_NAME'; value: string }
  | { type: 'UPDATE_LAST_NAME'; value: string }
  | { type: 'UPDATE_PHONE'; value: string }
  | { type: 'UPDATE_EXTENSION'; value: string }
  | { type: 'UPDATE_EMAIL'; value: string }
  | { type: 'UPDATE_COMMENT'; value: string };

const reducer = (
  state: ContactInformationFormType,
  action: ContactInformationFormActionType
) => {
  switch (action.type) {
    case 'UPDATE_FIRST_NAME':
      return {
        ...state,
        firstName: action.value,
      };
    case 'UPDATE_LAST_NAME':
      return {
        ...state,
        lastName: action.value,
      };
    case 'UPDATE_PHONE':
      return {
        ...state,
        phone: action.value,
      };
    case 'UPDATE_EXTENSION':
      return {
        ...state,
        extension: action.value,
      };
    case 'UPDATE_EMAIL':
      return {
        ...state,
        email: action.value,
      };
    case 'UPDATE_COMMENT':
      return {
        ...state,
        comment: action.value,
      };
    default:
      return state;
  }
};

export interface PartnerSignUpFlowProps {
  progress: number;
}

interface ContactInformationFormProps extends PartnerSignUpFlowProps {
  onSubmit: (values: Partial<PartnerSignUpFormFields>) => void;
  initialValues: ContactInformationFormFields;
}

export const ContactInformationForm: React.FC<ContactInformationFormProps> = ({
  onSubmit,
  progress,
  initialValues,
}) => {
  const [formData, dispatch] = React.useReducer(reducer, initialValues);

  const [formErrors, setFormErrors] = React.useState({
    firstName: FormErrors.Clear,
    lastName: FormErrors.Clear,
    phone: FormErrors.Clear,
    extension: FormErrors.Clear,
    email: FormErrors.Clear,
  });

  const validateContactInformationForm = useCallback(() => {
    let emailFormState = FormErrors.Clear;
    let phoneFormState = FormErrors.Clear;

    if (!formData.email) {
      emailFormState = FormErrors.Empty;
    } else if (!validateEmail(formData.email)) {
      emailFormState = FormErrors.INVALID_EMAIL;
    }

    if (!formData.phone) {
      phoneFormState = FormErrors.Empty;
    } else if (!validatePhone(formData.phone)) {
      phoneFormState = FormErrors.INVALID_PHONE;
    }

    const newFormErrors = {
      firstName: formData.firstName ? FormErrors.Clear : FormErrors.Empty,
      lastName: formData.lastName ? FormErrors.Clear : FormErrors.Empty,
      email: emailFormState,
      extension:
        formData.extension && formData.extension.length > 4
          ? FormErrors.INVALID_EMAIL
          : FormErrors.Clear,
      phone: phoneFormState,
    };

    setFormErrors({
      ...formErrors,
      ...newFormErrors,
    });
    return isFormValid(newFormErrors);
  }, [formData, formErrors]);

  const handleSubmit = useCallback(() => {
    const shouldSubmit = validateContactInformationForm();
    if (shouldSubmit) {
      onSubmit(formData);
    }
  }, [validateContactInformationForm, onSubmit, formData]);

  return (
    <section className="pb-[40px] pt-[20px]">
      <div className="mb-6">
        <span className="text-title24px font-semibold md:text-display32px lg:text-display48px">
          Enter contact information
        </span>
      </div>

      <div className="flex flex-col">
        <FormInput
          variant="primary"
          label="First name"
          className="mb-6"
          required
          errorMessage="Please input your first name"
          value={formData.firstName}
          onChange={(value: string) => {
            dispatch({ type: 'UPDATE_FIRST_NAME', value });
            setFormErrors({
              ...formErrors,
              firstName: FormErrors.Clear,
            });
          }}
          invalid={formErrors.firstName !== FormErrors.Clear}
        />
        <FormInput
          variant="primary"
          label="Last name"
          className="mb-6"
          required
          errorMessage="Please input your last name"
          value={formData.lastName}
          onChange={(value: string) => {
            dispatch({ type: 'UPDATE_LAST_NAME', value });
            setFormErrors({
              ...formErrors,
              lastName: FormErrors.Clear,
            });
          }}
          invalid={formErrors.lastName !== FormErrors.Clear}
        />
        <div className="mb-6 flex flex-row gap-[16px]">
          <FormInput
            className="flex-2 basis-2/3"
            variant="primary"
            label="Phone number"
            required
            errorMessage="Please input your phone number"
            value={formData.phone}
            onChange={(value: string) => {
              dispatch({ type: 'UPDATE_PHONE', value });
              setFormErrors({
                ...formErrors,
                phone: FormErrors.Clear,
              });
            }}
            invalid={formErrors.phone !== FormErrors.Clear}
          />
          <FormInput
            className="flex-1 basis-1/3"
            variant="primary"
            label="Ext."
            errorMessage="Invalid extension"
            value={formData.extension}
            onChange={(value: string) => {
              dispatch({ type: 'UPDATE_EXTENSION', value });
              setFormErrors({
                ...formErrors,
                extension: FormErrors.Clear,
              });
            }}
            invalid={formErrors.extension !== FormErrors.Clear}
          />
        </div>
        <FormInput
          variant="primary"
          label="Email"
          required
          errorMessage="Invalid email"
          className="mb-6"
          value={formData.email}
          onChange={(value: string) => {
            dispatch({ type: 'UPDATE_EMAIL', value });
            setFormErrors({
              ...formErrors,
              email: FormErrors.Clear,
            });
          }}
          invalid={
            formErrors.email === FormErrors.INVALID_EMAIL ||
            formErrors.email !== FormErrors.Clear
          }
        />
      </div>
      <FormTextarea
        label="Comments"
        labelClassname="text-body16px font-normal leading-5 text-neutral-800"
        className="mb-[48px]"
        value={formData.comment}
        onChange={(value: string) => {
          dispatch({ type: 'UPDATE_COMMENT', value });
        }}
      />
      <div className="flex flex-col">
        <div className="mb-6">
          <span className="text-micro12px text-neutral-600">
            By continuing, you agree to the{' '}
            <Link
              className="text-micro12px font-semibold underline"
              href="terms_and_conditions"
              target="_blank"
            >
              Agreement
            </Link>{' '}
            and{' '}
            <Link
              className="text-micro12px font-semibold underline"
              href="privacy_policy"
              target="_blank"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </div>

        <ProgressBar value={progress} max={100} className="mb-[20px]" />
        <Button onClick={handleSubmit} variant={ButtonVariants.newPrimary}>
          Submit
        </Button>
      </div>
    </section>
  );
};
